import { ChargingLocation } from '@vae/server/src/interfaces/eNode/eNode.interface'
import { SmartChargingStatus } from '@vae/server/src/interfaces/eNode/eNodeSmartCharging.interface'
import { ENodeVehicle } from '@vae/server/src/interfaces/eNode/eNodeVehicle.interface'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useUser from '../../../../contexts/hooks/useUser'
import { trpc } from '../../../../modules/trpc'
import { getInterventionIdsFromVehicle } from '../../../../utils/getInterventionIdsWithoutDuplicates'
import { useSmartChargeOverride } from './useSmartChargeOverride'

interface UseElectronicCarReturnType {
	vehicle: ENodeVehicle | null | undefined

	vehicleName: string
	vehicleLoading: boolean
	vehicleAtChargingLocation: ChargingLocation | undefined
	smartChargingStatus: SmartChargingStatus | null | undefined
	setSmartChargingPolicy: ({
		hashedPid,
		vehicleId,
		options,
	}: {
		hashedPid: string
		vehicleId: string
		options: {
			isEnabled: boolean
			deadline: string
		}
	}) => void
	interventionIds: string[] | undefined | null
	isError: boolean
	smartOverride: ReturnType<typeof useSmartChargeOverride>
}

export const useElectronicCar = (vehicleId: string): UseElectronicCarReturnType | null => {
	const utils = trpc.useContext()

	const { user } = useUser()

	const { t } = useTranslation('pages_electricCar')

	const errorStates = [
		'PLAN:ENDED:FAILED',
		'PLAN:EXECUTING:START_FAILED',
		'PLAN:EXECUTING:STOP_FAILED',
	]

	const { data: vehicle, isLoading: vehicleLoading } = trpc.eNode.vehicles.getSingle.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
			vehicleId,
		},
		{
			staleTime: 0, // Reduced stale time for immediate updates after mutations
			cacheTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
			refetchOnWindowFocus: false,
			enabled: !!vehicleId && !!user?.vehicleLinked && !!user?.hashedPid,
			onError(error) {
				console.error('error', error)
			},
		}
	)

	const { data: chargingLocations } = trpc.eNode.charging.stations.get.useQuery({
		hashedPid: user?.hashedPid ?? '',
	})

	const { data: smartChargingStatus } = trpc.eNode.charging.getSmartChargingStatus.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
			vehicleId: vehicle?.id ?? '',
		},
		{
			enabled: Boolean(vehicle?.id) && user?.vehicleLinked,
			staleTime: 0, // Reduced stale time for immediate updates
			cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
			refetchOnWindowFocus: false,
			onError(error) {
				console.error('error', error)
			},
		}
	)

	const { mutate: setSmartChargingPolicy } = trpc.eNode.charging.setSmartChargingPolicy.useMutation(
		{
			onError() {
				utils.eNode.vehicles.get.invalidate()
				utils.eNode.vehicles.getSingle.invalidate()
				utils.eNode.charging.getSmartChargingStatus.invalidate()
			},
			onSuccess() {
				utils.eNode.vehicles.get.invalidate()
				utils.eNode.vehicles.getSingle.invalidate()
				utils.eNode.charging.getSmartChargingStatus.invalidate()
			},
		}
	)

	const vehicleAtChargingLocation = useMemo(
		() => (chargingLocations ?? []).find(location => location.id === vehicle?.location.id),
		[chargingLocations, vehicle]
	)

	const interventionIds = useMemo(
		() => vehicle && getInterventionIdsFromVehicle(vehicle),

		[vehicle]
	)

	const smartOverride = useSmartChargeOverride(smartChargingStatus, vehicle)

	const vehicleName = useMemo(() => {
		if (vehicle) {
			return `${vehicle.information.brand}   ${vehicle.information.model} (${vehicle.information.year})`
		}
		return t('electricCarDetail.electricCar')
	}, [vehicle, t])

	const isError = useMemo(() => {
		return errorStates.includes(smartChargingStatus?.state || '')
	}, [smartChargingStatus?.state])

	return {
		vehicle,
		vehicleName,
		vehicleLoading,
		vehicleAtChargingLocation,
		smartChargingStatus,
		setSmartChargingPolicy,
		interventionIds,
		isError,
		smartOverride,
	}
}
